import { DestroyRef, inject, Injectable } from '@angular/core';
import { OidcSecurityService, PublicEventsService, EventTypes } from 'angular-auth-oidc-client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const SSO_ATTEMPT_KEY = 'sso_last_attempt';
const SSO_COOLDOWN_MS = 5000;
const SSO_LOGIN_REQUIRED_COOLDOWN_MS = 30000;

interface SsoAttemptData {
	timestamp: number;
	error?: string;
}

/**
 * Handles silent renew failures gracefully.
 * When refresh token is rejected (e.g. IS4 restart with in-memory store,
 * token expiry, revocation), attempts re-authorization via IS4 cookie session.
 * Cooldown prevents infinite redirect loops.
 * Pattern from Fusara.Workspace.UI SsoAuthService (simplified — no cookie check).
 */
@Injectable({ providedIn: 'root' })
export class AuthRecoveryService {
	private readonly _oidcService = inject(OidcSecurityService);
	private readonly _eventsService = inject(PublicEventsService);
	private readonly _destroyRef = inject(DestroyRef);

	initialize(): void {
		this.setupEventListeners();
		this.handleOidcErrorFromUrl();
	}

	private setupEventListeners(): void {
		this._eventsService.registerForEvents().pipe(
			takeUntilDestroyed(this._destroyRef)
		).subscribe(event => {
			if (event.type === EventTypes.SilentRenewFailed) {
				this.handleSilentRenewFailed();
			}
			if (event.type === EventTypes.NewAuthenticationResult && event.value?.isAuthenticated) {
				this.clearSsoAttempt();
			}
		});
	}

	private handleSilentRenewFailed(): void {
		console.warn('[Auth] Silent renew failed, attempting re-authorization...');
		if (this.canAttemptSso()) {
			this.setSsoAttempt({ timestamp: Date.now() });
			// prompt:none — IS4 checks cookie session without showing login UI
			this._oidcService.authorize(undefined, { customParams: { prompt: 'none' } });
		} else {
			console.warn('[Auth] SSO cooldown active, skipping re-auth attempt');
		}
	}

	/** Handles ?error=login_required from IS4 redirect after prompt:none fails */
	private handleOidcErrorFromUrl(): void {
		const params = new URLSearchParams(window.location.search);
		const error = params.get('error');

		if (error) {
			console.warn('[Auth] OIDC error from URL:', error);
			this.setSsoAttempt({ timestamp: Date.now(), error });
			// Clean error params from URL
			const url = new URL(window.location.href);
			url.searchParams.delete('error');
			url.searchParams.delete('error_description');
			window.history.replaceState({}, '', url.pathname + (url.search || ''));

			// If prompt:none failed with login_required, do a full login
			if (error === 'login_required' && this.canAttemptSso()) {
				this.setSsoAttempt({ timestamp: Date.now(), error });
				this._oidcService.authorize();
			}
		}
	}

	private canAttemptSso(): boolean {
		const data = this.getSsoAttempt();
		if (!data) return true;

		const elapsed = Date.now() - data.timestamp;
		if (data.error === 'login_required') {
			return elapsed > SSO_LOGIN_REQUIRED_COOLDOWN_MS;
		}
		return elapsed > SSO_COOLDOWN_MS;
	}

	private getSsoAttempt(): SsoAttemptData | null {
		const raw = sessionStorage.getItem(SSO_ATTEMPT_KEY);
		if (!raw) return null;
		try { return JSON.parse(raw); } catch { return null; }
	}

	private setSsoAttempt(data: SsoAttemptData): void {
		sessionStorage.setItem(SSO_ATTEMPT_KEY, JSON.stringify(data));
	}

	private clearSsoAttempt(): void {
		sessionStorage.removeItem(SSO_ATTEMPT_KEY);
	}
}
